import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { PwTestIdDirective } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { AskDecisionOption } from '@core/tellent-assistant-types';
import { ASK_DECISION_TEST_IDS } from '@core/tellent-assistant-util';
import type { RadioGroupItem, Select2Item } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  RadioFormGroupComponent,
  Select2Component,
  Select2TriggerComponent,
} from '@core/user-interface-legacy';

const DROPDOWN_THRESHOLD = 5;

/**
 * A question the assistant asks mid-conversation, answered by picking one
 * option. Few options render as a radio list, many as a dropdown; the answer
 * is emitted on confirm and sent back as a plain user turn.
 */
@Component({
  selector: 'tellent-assistant-ask-decision',
  imports: [
    ButtonComponent,
    PwTestIdDirective,
    RadioFormGroupComponent,
    ReactiveFormsModule,
    Select2Component,
    Select2TriggerComponent,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="card" [pwTestId]="testIds.card">
      <h4 class="question">{{ question() }}</h4>

      @if (useDropdown()) {
        <rt-select2
          class="select"
          withOverlayLevel
          [pwTestId]="testIds.dropdownTrigger"
          [items]="selectItems()"
          [disabled]="disabled()"
          (selection)="onSelection($event)"
        >
          <rt-select-trigger
            [placeholder]="'tlc.tellent_assistant.decision.select_placeholder' | translate"
          />
        </rt-select2>
      } @else {
        <rt-radio-form-group
          class="options"
          [items]="radioItems()"
          [formControl]="answerControl"
          [disabled]="disabled()"
        />
      }

      <footer class="actions">
        <rt-button
          variant="plain"
          size="small"
          [pwTestId]="testIds.skip"
          [disabled]="disabled()"
          (click)="skipped.emit()"
        >
          {{ 'tlc.tellent_assistant.proposal.skip' | translate }}
        </rt-button>
        <rt-button
          variant="primary"
          size="small"
          [pwTestId]="testIds.confirm"
          [disabled]="disabled() || !selected()"
          (click)="confirm()"
        >
          {{ confirmText() }}
        </rt-button>
      </footer>
    </section>
  `,
  styleUrl: './ask-decision.component.less',
})
export class TellentAssistantAskDecisionComponent {
  private readonly i18n = inject(I18nService);

  protected readonly testIds = ASK_DECISION_TEST_IDS;

  protected readonly answerControl = new FormControl<string | null>(null);

  protected readonly selected = toSignal(this.answerControl.valueChanges, { initialValue: null });

  protected readonly useDropdown = computed(() => this.options().length >= DROPDOWN_THRESHOLD);

  protected readonly selectItems = computed(
    (): Array<Select2Item<string>> => this.options().map(o => ({ label: o.label, value: o.value })),
  );

  protected readonly radioItems = computed(
    (): Array<RadioGroupItem<string>> =>
      this.options().map(o => ({ option: o.label, value: o.value })),
  );

  protected readonly confirmText = computed(
    () => this.confirmLabel() ?? this.i18n.translate('tlc.tellent_assistant.decision.continue'),
  );

  public readonly question = input.required<string>();
  public readonly options = input.required<AskDecisionOption[]>();
  public readonly confirmLabel = input<string | null>(null);
  public readonly disabled = input<boolean>(false);

  public readonly answered = output<AskDecisionOption>();
  public readonly skipped = output<void>();

  protected onSelection(item: Select2Item<string>): void {
    this.answerControl.setValue(item.value);
  }

  protected confirm(): void {
    const value = this.selected();
    const option = this.options().find(o => o.value === value);
    if (option) this.answered.emit(option);
  }
}
